import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import ProgressivePhoto from '../../../../../../../../../components/grid/ProgressivePhoto';
import { IMAGE_SIZE_CODE } from '../../../../../../../../../constants/image-size-codes';
import { IMAGE_BLUR_TYPES } from '../../../../../../../../../constants/image-blur-types';
import WatermarkOverlay from '../../../../../../../../../components/global/WatermarkOverlay';
import { MATCHED_PHOTO_WATERMARK_MODE } from '../../../../../../../../../constants/pro-settings';

const propTypes = {
  className: PropTypes.string,
  photo: PropTypes.shape({
    id: PropTypes.string.isRequired,
    thumbnail_urls: PropTypes.array.isRequired,
    is_entitled_matched_photo: PropTypes.bool,
  }).isRequired,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
};

const PurchasingOptionsPhotoPreview = ({
  className,
  photo,
  watermarkSettings,
}) => {
  const classes = classNames({
    'purchasing-options-photo-preview': true,
    'pos-rel': true,
    [className]: !!className,
  });

  const isPictureDay = photo?.is_picture_day_enabled;

  const isEntitled =
    typeof photo?.is_entitled_matched_photo === 'undefined' ||
    photo?.is_entitled_matched_photo;

  const watermarkedThumbnails = useMemo(() => {
    return photo?.wallpaper_watermark_urls;
  }, [photo]);

  const foregroundThumbnails = useMemo(() => {
    if (!photo?.foreground?.original_url) {
      return [];
    }

    if (
      !isEntitled &&
      watermarkSettings?.watermarkMode ===
        MATCHED_PHOTO_WATERMARK_MODE.WALLPAPER &&
      photo?.foreground?.watermarked_thumbnail_urls?.length > 0
    ) {
      return photo?.foreground?.watermarked_thumbnail_urls;
    }

    if (
      !isEntitled &&
      photo?.faceblocked_thumbnail_urls?.find(
        ({ blur_type: blurType }) =>
          blurType === IMAGE_BLUR_TYPES.PHOTO_FOREGROUND_FOREGROUND,
      )
    ) {
      return photo?.faceblocked_thumbnail_urls?.filter(
        ({ blur_type: blurType }) =>
          blurType === IMAGE_BLUR_TYPES.PHOTO_FOREGROUND_FOREGROUND,
      );
    }

    if (
      !isEntitled &&
      photo?.foreground?.watermarked_thumbnail_urls?.length > 0
    ) {
      return photo?.foreground?.watermarked_thumbnail_urls;
    }

    if (photo?.foreground?.thumbnail_urls?.length > 0) {
      return photo?.foreground?.thumbnail_urls;
    }

    return [photo?.foreground?.original_url];
  }, [photo, isEntitled]);

  const thumbnails = useMemo(() => {
    if (foregroundThumbnails.length > 0) {
      return foregroundThumbnails;
    }

    if (
      !isEntitled &&
      watermarkSettings?.watermarkMode ===
        MATCHED_PHOTO_WATERMARK_MODE.WALLPAPER &&
      watermarkedThumbnails?.length > 0
    ) {
      return watermarkedThumbnails;
    }

    if (isPictureDay && !isEntitled) {
      return (
        photo.faceblocked_thumbnail_urls?.filter(
          thumb => thumb.blur_type === IMAGE_BLUR_TYPES.FOREGROUND,
        ) ?? photo.thumbnail_urls
      );
    }

    return photo.thumbnail_urls;
  }, [photo, isPictureDay, isEntitled]);

  const backgroundStyles = useMemo(() => {
    if (photo?.print_background?.id) {
      return {
        backgroundImage: `url(${photo.print_background.asset_image.url})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
      };
    }

    return {};
  }, [photo]);

  return (
    <div className={classes}>
      <ProgressivePhoto
        style={{ ...backgroundStyles }}
        className="purchasing-options-photo-preview-photo"
        size={isEntitled ? IMAGE_SIZE_CODE.MEDIUM2X : IMAGE_SIZE_CODE.MEDIUM}
        thumbnails={thumbnails}
        photoId={photo?.id}
        allowFallbackThumbnail
      />
      {!isEntitled &&
        !isPictureDay &&
        !photo?.foreground?.original_url &&
        thumbnails?.[0]?.id !== watermarkedThumbnails?.[0]?.id && (
          <div className="watermark-container">
            <WatermarkOverlay
              text={watermarkSettings?.wallpaperCustomText}
              fontSize={watermarkSettings?.wallpaperFontSize}
              wallpaperMode={watermarkSettings?.wallpaperWatermarkMode}
              imageUrl={watermarkSettings?.wallpaperAssetImage?.url}
            />
          </div>
        )}
    </div>
  );
};

PurchasingOptionsPhotoPreview.propTypes = propTypes;
export default PurchasingOptionsPhotoPreview;
